import {
    type AddressFormatMetadata,
    type AdministrativeAreaType,
    type DependentLocalityType,
    AddressField,
    LocalityType,
    PostalCodeType,
} from '../generated/types';

import {
    toExtendedAddressInputKey,
    isAddressInputKey,
    type AddressFieldWithCountry,
    type ExtendedAddressInputKey,
    type AddressInputKey,
} from '../lib/address-input';
import { isAddressField } from './enum-typeguards';

export class AddressFormat {
    public readonly __typename = 'AddressFormat' as const;
    public readonly format: string;
    public readonly localFormat: string | null;
    public readonly localityType: LocalityType | null;
    public readonly dependentLocalityType: DependentLocalityType | null;
    public readonly administrativeAreaType: AdministrativeAreaType | null;
    public readonly postalCodePattern: string | null;
    public readonly postalCodePrefix: string | null;
    public readonly postalCodeType: PostalCodeType | null;
    public readonly requiredFields: ReadonlyArray<AddressField>;
    public readonly uppercaseFields: ReadonlyArray<AddressField>;
    public readonly usedFields: ReadonlyArray<AddressField>;
    public readonly usedSubdivisionFields: ReadonlyArray<AddressField>;
    public readonly subdivisionDepth: number;

    constructor(metadata: AddressFormatMetadata) {
        this.format = metadata.format;
        this.localFormat = metadata.localFormat;
        this.localityType = metadata.localityType;
        this.dependentLocalityType = metadata.dependentLocalityType;
        this.administrativeAreaType = metadata.administrativeAreaType;
        this.postalCodePattern = metadata.postalCodePattern;
        this.postalCodePrefix = metadata.postalCodePrefix;
        this.postalCodeType = metadata.postalCodeType;
        this.requiredFields = metadata.requiredFields;
        this.uppercaseFields = metadata.uppercaseFields;
        this.usedFields = metadata.usedFields;
        this.usedSubdivisionFields = metadata.usedSubdivisionFields;
        this.subdivisionDepth = metadata.subdivisionDepth;
    }

    public static default(): AddressFormat {
        return new AddressFormat({
            format: '%ORGANIZATION\n%GIVEN_NAME %FAMILY_NAME\n%ADDRESS_LINE1\n%ADDRESS_LINE2\n%POSTAL_CODE %LOCALITY',
            localFormat: null,
            localityType: LocalityType.CITY,
            dependentLocalityType: null,
            administrativeAreaType: null,
            postalCodePattern: '^d{4} ?[A-Za-z]{2}$',
            postalCodePrefix: null,
            postalCodeType: PostalCodeType.POSTAL,
            requiredFields: [
                AddressField.ADDRESS_LINE1,
                AddressField.POSTAL_CODE,
                AddressField.LOCALITY,
            ],
            uppercaseFields: [],
            usedSubdivisionFields: [],
            usedFields: [
                AddressField.ORGANIZATION,
                AddressField.GIVEN_NAME,
                AddressField.FAMILY_NAME,
                AddressField.ADDRESS_LINE1,
                AddressField.ADDRESS_LINE2,
                AddressField.POSTAL_CODE,
                AddressField.LOCALITY,
            ],
            subdivisionDepth: 0,
        });
    }

    public get formatFields(): AddressField[][] {
        return formatToFormatFields(this.format);
    }

    public get formatAddressInputKeys(): AddressInputKey[][] {
        return this.formatExtendedAddressInputKeys
            .map((keysLine) => keysLine.filter(isAddressInputKey))
            .filter((keysLine) => keysLine.length > 0);
    }

    public get formatExtendedAddressInputKeys(): ExtendedAddressInputKey[][] {
        const result = this.formatFields.map((fieldsLine) =>
            fieldsLine.map(toExtendedAddressInputKey)
        );
        result.push(['countryCode']);
        return result;
    }

    public get localFormatFields(): AddressField[][] {
        return formatToFormatFields(this.localFormat);
    }

    public get localFormatAddressInputKeys(): AddressInputKey[][] {
        return this.localFormatExtendedAddressInputKeys
            .map((keysLine) => keysLine.filter(isAddressInputKey))
            .filter((keysLine) => keysLine.length > 0);
    }

    public get localFormatExtendedAddressInputKeys(): ExtendedAddressInputKey[][] {
        const result = this.localFormatFields.map((fieldsLine) =>
            fieldsLine.map(toExtendedAddressInputKey)
        );
        result.push(['countryCode']);
        return result;
    }

    public get postalCodeRegExp() {
        return this.postalCodePattern
            ? new RegExp(this.postalCodePattern)
            : /^$/;
    }

    public testPostalCode(postalCode: string) {
        if (this.postalCodePattern === null) return true;
        return this.postalCodeRegExp.test(postalCode);
    }

    public get uppercaseAddressInputKeys() {
        return this.uppercaseExtendedAddressInputKeys.filter(isAddressInputKey);
    }

    public get uppercaseExtendedAddressInputKeys() {
        return this.uppercaseFields.map(toExtendedAddressInputKey);
    }

    public uppercase(field: AddressFieldWithCountry | ExtendedAddressInputKey) {
        const inputKey = toExtendedAddressInputKey(field);
        return this.uppercaseExtendedAddressInputKeys.includes(inputKey);
    }

    public get usedAddressInputKeys() {
        return this.usedExtendedAddressInputKeys.filter(isAddressInputKey);
    }

    public get usedExtendedAddressInputKeys() {
        return this.usedFields.map(toExtendedAddressInputKey);
    }

    public uses(field: AddressFieldWithCountry | ExtendedAddressInputKey) {
        const inputKey = toExtendedAddressInputKey(field);
        if (field === 'countryCode') return true;
        return this.usedExtendedAddressInputKeys.includes(inputKey);
    }

    public get usedSubdivisionAddressInputKeys() {
        return this.usedSubdivistionExtendedAddressInputKeys.filter(
            isAddressInputKey
        );
    }

    public get usedSubdivistionExtendedAddressInputKeys() {
        return this.usedSubdivisionFields.map(toExtendedAddressInputKey);
    }

    public usesSubdivistion(
        field: AddressFieldWithCountry | ExtendedAddressInputKey
    ) {
        const inputKey = toExtendedAddressInputKey(field);
        return this.usedSubdivistionExtendedAddressInputKeys.includes(inputKey);
    }

    public get requiredAddressInputKeys() {
        return this.requiredExtendedAddressInputKeys.filter(isAddressInputKey);
    }

    public get requiredExtendedAddressInputKeys() {
        return this.requiredFields.map(toExtendedAddressInputKey);
    }

    public requires(field: AddressFieldWithCountry | ExtendedAddressInputKey) {
        const inputKey = toExtendedAddressInputKey(field);
        if (field === 'countryCode') return true;
        return this.requiredExtendedAddressInputKeys.includes(inputKey);
    }
}

function formatToFormatFields(format: string | null): AddressField[][] {
    if (!format) return [];

    const formatLines = format.split('\n');

    return formatLines.map((line) => {
        const fields: AddressField[] = [];
        for (const match of line.matchAll(/%(?<field>[A-Z0-9_]+)/g)) {
            const field = match.groups?.['field'];
            if (!isAddressField(field)) {
                throw new Error(`Unknown address field ${field}.`);
            }
            fields.push(field);
        }
        return fields;
    });
}

export function createAddressFormat(
    metadata: AddressFormatMetadata
): AddressFormat {
    return new AddressFormat(metadata);
}
